/* iter55x — Starter Templates admin panel.
 *
 * Edits `app_config.starter_templates` — the catalog that powers the
 * "New from Template" flow surfaced in the Welcome Overlay and the
 * File menu. Backend endpoint: PUT /api/admin/config with
 *   { starter_templates: [{ id, name, tagline, icon, chapters, characters }] }
 *
 * Chapters + characters are edited as raw JSON textareas to keep the
 * surface small; a full row-editor is deferred until real users start
 * asking for it. Basic validation runs in-browser before the save call.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { adminApi } from '@/lib/api';
import { toast } from 'sonner';
import { Save, Plus, Trash2, Loader2, FileCode2, RefreshCcw, FileJson } from 'lucide-react';
import BeatMapImportDialog from './BeatMapImportDialog';
import ImportHistoryStrip from './ImportHistoryStrip';

function safeParseJson(raw, fallback) {
  try {
    if (!raw || !raw.trim()) return fallback;
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v : fallback;
  } catch {
    return null;   // parse error
  }
}

function TemplateRow({ template, idx, onChange, onRemove }) {
  const set = (patch) => onChange({ ...template, ...patch });
  const chaptersRaw = template._chaptersRaw ?? JSON.stringify(template.chapters || [], null, 2);
  const charactersRaw = template._charactersRaw ?? JSON.stringify(template.characters || [], null, 2);
  return (
    <div className="rounded-md border border-border bg-card p-3 shadow-panel space-y-2"
         data-testid={`admin-starter-template-row-${idx}`}>
      <div className="flex items-start justify-between gap-2">
        <FileCode2 className="mt-1 h-4 w-4 text-primary" />
        <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-3">
          <div>
            <Label className="text-[10.5px] uppercase tracking-wider">ID (slug)</Label>
            <Input value={template.id || ''}
                   onChange={(e) => set({ id: e.target.value.replace(/[^a-z0-9_-]/gi, '').toLowerCase() })}
                   placeholder="regency-romance"
                   data-testid={`admin-starter-template-id-${idx}`} />
          </div>
          <div>
            <Label className="text-[10.5px] uppercase tracking-wider">Display name</Label>
            <Input value={template.name || ''}
                   onChange={(e) => set({ name: e.target.value })}
                   placeholder="Regency Romance"
                   data-testid={`admin-starter-template-name-${idx}`} />
          </div>
          <div>
            <Label className="text-[10.5px] uppercase tracking-wider">Icon (lucide name)</Label>
            <Input value={template.icon || 'BookOpen'}
                   onChange={(e) => set({ icon: e.target.value })}
                   placeholder="BookOpen"
                   data-testid={`admin-starter-template-icon-${idx}`} />
          </div>
        </div>
        <Button type="button" variant="ghost" size="sm"
                 onClick={onRemove} className="text-destructive"
                 data-testid={`admin-starter-template-delete-${idx}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div>
        <Label className="text-[10.5px] uppercase tracking-wider">Tagline (400 chars max)</Label>
        <Input value={template.tagline || ''}
               onChange={(e) => set({ tagline: e.target.value })}
               maxLength={400}
               placeholder="A one-line pitch for what this framework kickstarts."
               data-testid={`admin-starter-template-tagline-${idx}`} />
      </div>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <div>
          <Label className="text-[10.5px] uppercase tracking-wider">
            Chapters (JSON array of {"{ title, status, seed }"})
          </Label>
          <Textarea rows={5}
                     value={chaptersRaw}
                     onChange={(e) => set({ _chaptersRaw: e.target.value })}
                     className="font-mono text-[11px]"
                     data-testid={`admin-starter-template-chapters-${idx}`} />
        </div>
        <div>
          <Label className="text-[10.5px] uppercase tracking-wider">
            Characters (JSON array of {"{ name, archetype, traits }"})
          </Label>
          <Textarea rows={5}
                     value={charactersRaw}
                     onChange={(e) => set({ _charactersRaw: e.target.value })}
                     className="font-mono text-[11px]"
                     data-testid={`admin-starter-template-characters-${idx}`} />
        </div>
      </div>
    </div>
  );
}

export default function StarterTemplatesAdminPanel() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  // Stable per-row key generator. React re-uses DOM by key, so when the
  // user deletes row #3 of a controlled-input list we MUST re-key the
  // rows to prevent row #4's textarea state bleeding into row #3.
  const nextKeyRef = React.useRef(1);
  const withKey = (t) => ({ _rk: t._rk || `rk_${nextKeyRef.current++}`, ...t });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cfg = await adminApi.config();
      const rows = (cfg?.starter_templates || []).map(withKey);
      setTemplates(rows);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load config.');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addRow = () => {
    setTemplates((prev) => [...prev, withKey({
      id: '', name: '', tagline: '', icon: 'BookOpen',
      chapters: [{ title: 'Chapter 1', status: 'planned', seed: '' }],
      characters: [],
    })]);
  };

  const updateRow = (idx, patch) => {
    setTemplates((prev) => prev.map((t, i) => (i === idx ? { ...patch, _rk: t._rk } : t)));
  };
  const removeRow = (idx) => {
    setTemplates((prev) => prev.filter((_, i) => i !== idx));
  };

  const save = async () => {
    // Normalize + validate.
    const cleaned = [];
    for (let i = 0; i < templates.length; i += 1) {
      const t = templates[i];
      if (!(t.id || '').trim() || !(t.name || '').trim()) {
        toast.error(`Row ${i + 1}: id and name are required.`);
        return;
      }
      const chapters = t._chaptersRaw !== undefined
        ? safeParseJson(t._chaptersRaw, [])
        : (t.chapters || []);
      if (chapters === null) {
        toast.error(`Row ${i + 1}: chapters is not valid JSON.`);
        return;
      }
      if (!Array.isArray(chapters) || chapters.length < 1) {
        toast.error(`Row ${i + 1}: needs at least one chapter.`);
        return;
      }
      const characters = t._charactersRaw !== undefined
        ? safeParseJson(t._charactersRaw, [])
        : (t.characters || []);
      if (characters === null) {
        toast.error(`Row ${i + 1}: characters is not valid JSON.`);
        return;
      }
      cleaned.push({
        id:         t.id.trim(),
        name:       t.name.trim(),
        tagline:    (t.tagline || '').trim(),
        icon:       (t.icon || 'BookOpen').trim(),
        chapters,
        characters,
      });
    }
    setSaving(true);
    try {
      await adminApi.saveConfig({ starter_templates: cleaned });
      toast.success('Starter templates saved.');
      // Re-attach stable keys so React doesn't remount every row.
      setTemplates(cleaned.map(withKey));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save.');
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-6 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading templates…
      </div>
    );
  }
  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4" data-testid="admin-starter-templates-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[18px] font-semibold tracking-tight">Starter Templates Registry</h2>
          <p className="mt-0.5 text-[12px] text-muted-foreground">
            Curated frameworks shown in the Welcome overlay and File → New from Template. Add, edit, or remove without shipping code.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={load} className="gap-1.5"
                   data-testid="admin-starter-templates-reload">
            <RefreshCcw className="h-3.5 w-3.5" /> Reload
          </Button>
          <Button size="sm" variant="outline" onClick={() => setImportOpen(true)} className="gap-1.5"
                   data-testid="admin-starter-templates-import-beatmap">
            <FileJson className="h-3.5 w-3.5" /> Import beat map
          </Button>
          <Button size="sm" variant="outline" onClick={addRow} className="gap-1.5"
                   data-testid="admin-starter-templates-add">
            <Plus className="h-3.5 w-3.5" /> Add template
          </Button>
          <Button size="sm" onClick={save} disabled={saving} className="gap-1.5"
                   data-testid="admin-starter-templates-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save all
          </Button>
        </div>
      </div>
      <ImportHistoryStrip refreshKey={importOpen ? 0 : refreshTick} />
      <BeatMapImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={() => { load(); setRefreshTick((n) => n + 1); }}
      />
      {templates.length === 0 && (
        <div className="rounded-md border border-dashed border-border p-8 text-center text-[12px] text-muted-foreground">
          No templates yet. Click <b>Add template</b> to seed the registry.
        </div>
      )}
      <div className="space-y-3">
        {templates.map((t, idx) => (
          <TemplateRow key={t._rk}
                        template={t}
                        idx={idx}
                        onChange={(next) => updateRow(idx, next)}
                        onRemove={() => removeRow(idx)} />
        ))}
      </div>
    </div>
  );
}
